import type { ShapeType } from '../types';

/**
 * Preset karakter motoru.
 *
 * Bir preset, sahnedeki bir group nesnesinin hangi parçalardan (shape child)
 * kurulacağını tarif eder. Her parça bugünkü shape tiplerinden biridir
 * (rectangle / circle / triangle / path); özel bir çizim yolu yoktur.
 * Seed, paleti ve küçük oran farklarını belirler; parça listesi preset'e aittir.
 */

export type CharacterPresetId = 'humanoid' | 'animal' | 'turtle' | 'octopus';

export const CHARACTER_PRESET_IDS: CharacterPresetId[] = ['humanoid', 'animal', 'turtle', 'octopus'];

/** Palet rolleri — parça rengi bu rollerden birine bağlanır. */
type PaletteRole = 'skin' | 'hair' | 'body' | 'bodyShade' | 'accent' | 'limb' | 'dark' | 'light';

type CharacterPalette = Record<PaletteRole, string>;

/** Yol şekilleri için kutu boyutuna göre path üreten fonksiyon. */
type PathBuilder = (w: number, h: number) => string;

interface PartSpec {
  /** Rol adı; child nesne adı olur (`head`, `arm.left`). */
  role: string;
  shape: ShapeType;
  path?: PathBuilder;
  /** Tasarım kutusundaki yerel konum ve boyut (px). */
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  pivot?: string;
  color: PaletteRole;
  /** Sadece çizgi (kuyruk, ağız, kol). */
  strokeOnly?: boolean;
  strokeWidth?: number;
  /** Dış çizgi olmasın (göz bebeği gibi küçük parçalar). */
  noOutline?: boolean;
  /** Seed'e göre hiç konmayabilir. */
  optional?: boolean;
  /** Boyut titremesine katılma (göz, ağız). */
  rigid?: boolean;
  z: number;
}

interface PresetSpec {
  id: CharacterPresetId;
  /** Tasarım kutusu (px). Parça koordinatları bu kutuya göredir. */
  width: number;
  height: number;
  palettes: CharacterPalette[];
  parts: PartSpec[];
}

export interface GeneratedPart {
  role: string;
  shapeType: ShapeType;
  pathData?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  pivotPoint: string;
  fill?: string;
  fillMode: 'solid' | 'none';
  stroke: string;
  strokeWidth: number;
  strokeStyle: 'solid' | 'none';
  zIndex: number;
}

export interface GeneratedCharacter {
  preset: CharacterPresetId;
  seed: number;
  width: number;
  height: number;
  parts: GeneratedPart[];
}

// ---------------------------------------------------------------------------
// Yol yardımcıları
// ---------------------------------------------------------------------------

const f = (n: number) => Math.round(n * 100) / 100;

/** Üstü yuvarlak, altı düz kubbe (saç, kabuk kenarı). */
const dome: PathBuilder = (w, h) =>
  `M 0 ${f(h)} L 0 ${f(h * 0.55)} Q 0 0 ${f(w / 2)} 0 Q ${f(w)} 0 ${f(w)} ${f(h * 0.55)} L ${f(w)} ${f(h)} Z`;

/** Aşağı bakan üçgen (kravat, diş). */
const downTriangle: PathBuilder = (w, h) => `M 0 0 L ${f(w)} 0 L ${f(w / 2)} ${f(h)} Z`;

/** Sola bakan üçgen (kuyruk ucu). */
const leftTriangle: PathBuilder = (w, h) => `M ${f(w)} 0 L 0 ${f(h / 2)} L ${f(w)} ${f(h)} Z`;

/** Gülümseme: sol üstten sağ üste, ortası aşağı sarkan yay. */
const smile: PathBuilder = (w, h) => `M 0 0 Q ${f(w / 2)} ${f(h * 2)} ${f(w)} 0`;

/** Düz ağız. */
const flatMouth: PathBuilder = (w, h) => `M 0 ${f(h / 2)} L ${f(w)} ${f(h / 2)}`;

/** Sağ alttan sol üste kıvrılan kuyruk. */
const curlTail: PathBuilder = (w, h) => `M ${f(w)} ${f(h)} Q 0 ${f(h)} ${f(w * 0.15)} 0`;

/** Ahtapot kolu: üst ortadan başlayıp sola kıvrılarak aşağı iner. */
const armCurlLeft: PathBuilder = (w, h) =>
  `M ${f(w / 2)} 0 Q ${f(w * 0.05)} ${f(h * 0.55)} ${f(w * 0.85)} ${f(h)}`;

/** Ahtapot kolu: sağa kıvrılan eş. */
const armCurlRight: PathBuilder = (w, h) =>
  `M ${f(w / 2)} 0 Q ${f(w * 0.95)} ${f(h * 0.55)} ${f(w * 0.15)} ${f(h)}`;

// ---------------------------------------------------------------------------
// Presetler
// ---------------------------------------------------------------------------

const HUMANOID: PresetSpec = {
  id: 'humanoid',
  width: 200,
  height: 280,
  palettes: [
    { skin: '#f3c7a6', hair: '#3a2a24', body: '#4a5260', bodyShade: '#343b48', accent: '#e25b4c', limb: '#1c2330', dark: '#2a211c', light: '#e7edf3' },
    { skin: '#d9a070', hair: '#1f1a17', body: '#2f6f8f', bodyShade: '#255a73', accent: '#f2c14e', limb: '#2b2d42', dark: '#1f1a17', light: '#f3f6f8' },
    { skin: '#f7d9c4', hair: '#c96f2f', body: '#7a9e5b', bodyShade: '#5e7c45', accent: '#d94f6a', limb: '#3b3a4a', dark: '#2a211c', light: '#fbfbf5' },
    { skin: '#8d5a3b', hair: '#111010', body: '#a63d40', bodyShade: '#832f31', accent: '#f0e4c2', limb: '#1f2430', dark: '#1a1412', light: '#f4ede0' },
    { skin: '#ffd8b8', hair: '#e6d04f', body: '#5b4b8a', bodyShade: '#473a6e', accent: '#ff9f68', limb: '#2e2e3a', dark: '#2a211c', light: '#f3f0ff' },
  ],
  parts: [
    { role: 'leg.left', shape: 'rectangle', x: 68, y: 184, width: 24, height: 80, color: 'limb', pivot: 'top', z: 1 },
    { role: 'leg.right', shape: 'rectangle', x: 108, y: 184, width: 24, height: 80, color: 'limb', pivot: 'top', z: 1 },
    { role: 'foot.left', shape: 'rectangle', x: 62, y: 262, width: 34, height: 14, color: 'dark', z: 1 },
    { role: 'foot.right', shape: 'rectangle', x: 104, y: 262, width: 34, height: 14, color: 'dark', z: 1 },
    { role: 'arm.left', shape: 'rectangle', x: 40, y: 110, width: 18, height: 68, color: 'bodyShade', pivot: 'top', z: 1 },
    { role: 'arm.right', shape: 'rectangle', x: 142, y: 110, width: 18, height: 68, color: 'bodyShade', pivot: 'top', z: 1 },
    { role: 'hand.left', shape: 'circle', x: 39, y: 174, width: 20, height: 20, color: 'skin', z: 1 },
    { role: 'hand.right', shape: 'circle', x: 141, y: 174, width: 20, height: 20, color: 'skin', z: 1 },
    { role: 'torso', shape: 'rectangle', x: 60, y: 104, width: 80, height: 82, color: 'body', z: 2 },
    { role: 'collar', shape: 'path', path: downTriangle, x: 90, y: 104, width: 20, height: 16, color: 'light', noOutline: true, z: 3 },
    { role: 'tie', shape: 'path', path: downTriangle, x: 95, y: 110, width: 10, height: 28, color: 'accent', noOutline: true, optional: true, z: 3 },
    { role: 'head', shape: 'circle', x: 60, y: 24, width: 80, height: 84, color: 'skin', pivot: 'bottom', z: 4 },
    { role: 'hair', shape: 'path', path: dome, x: 56, y: 18, width: 88, height: 44, color: 'hair', optional: true, z: 5 },
    { role: 'eye.left', shape: 'circle', x: 80, y: 58, width: 10, height: 12, color: 'dark', noOutline: true, rigid: true, z: 6 },
    { role: 'eye.right', shape: 'circle', x: 110, y: 58, width: 10, height: 12, color: 'dark', noOutline: true, rigid: true, z: 6 },
    { role: 'mouth', shape: 'path', path: smile, x: 90, y: 84, width: 20, height: 6, color: 'dark', strokeOnly: true, strokeWidth: 2, rigid: true, z: 6 },
  ],
};

const ANIMAL: PresetSpec = {
  id: 'animal',
  width: 300,
  height: 200,
  palettes: [
    { skin: '#f0c9a8', hair: '#6b4a2b', body: '#c98a4b', bodyShade: '#a66f38', accent: '#f6e2c8', limb: '#a66f38', dark: '#2a211c', light: '#fff6ea' },
    { skin: '#e6e6e6', hair: '#444', body: '#8e8e93', bodyShade: '#6f6f74', accent: '#f4c2c2', limb: '#6f6f74', dark: '#1f1a17', light: '#f7f7f7' },
    { skin: '#ffe1b5', hair: '#2b2b2b', body: '#f2a541', bodyShade: '#d58a2c', accent: '#fff1d6', limb: '#d58a2c', dark: '#2a211c', light: '#fff8ec' },
    { skin: '#efd6c3', hair: '#3b2a1a', body: '#4f4f4f', bodyShade: '#383838', accent: '#f2e9e4', limb: '#383838', dark: '#141414', light: '#f6f1ec' },
  ],
  parts: [
    { role: 'tail', shape: 'path', path: curlTail, x: 20, y: 40, width: 60, height: 70, color: 'body', strokeOnly: true, strokeWidth: 12, pivot: 'bottom-right', z: 1 },
    { role: 'leg.back.far', shape: 'rectangle', x: 110, y: 128, width: 20, height: 56, color: 'bodyShade', pivot: 'top', z: 1 },
    { role: 'leg.front.far', shape: 'rectangle', x: 210, y: 128, width: 20, height: 56, color: 'bodyShade', pivot: 'top', z: 1 },
    { role: 'body', shape: 'circle', x: 70, y: 68, width: 170, height: 84, color: 'body', z: 2 },
    { role: 'leg.back.near', shape: 'rectangle', x: 84, y: 132, width: 22, height: 58, color: 'body', pivot: 'top', z: 3 },
    { role: 'leg.front.near', shape: 'rectangle', x: 184, y: 132, width: 22, height: 58, color: 'body', pivot: 'top', z: 3 },
    { role: 'ear.left', shape: 'triangle', x: 206, y: 8, width: 26, height: 32, color: 'bodyShade', pivot: 'bottom', z: 3 },
    { role: 'ear.right', shape: 'triangle', x: 248, y: 8, width: 26, height: 32, color: 'bodyShade', pivot: 'bottom', z: 3 },
    { role: 'head', shape: 'circle', x: 200, y: 28, width: 82, height: 78, color: 'body', pivot: 'bottom-left', z: 4 },
    { role: 'muzzle', shape: 'circle', x: 244, y: 64, width: 44, height: 34, color: 'accent', optional: true, z: 5 },
    { role: 'eye', shape: 'circle', x: 246, y: 50, width: 10, height: 10, color: 'dark', noOutline: true, rigid: true, z: 6 },
    { role: 'nose', shape: 'circle', x: 274, y: 70, width: 12, height: 10, color: 'dark', noOutline: true, rigid: true, z: 6 },
  ],
};

const TURTLE: PresetSpec = {
  id: 'turtle',
  width: 300,
  height: 180,
  palettes: [
    { skin: '#8fbf6a', hair: '#000', body: '#5a7d3a', bodyShade: '#3f5a28', accent: '#7fa34f', limb: '#8fbf6a', dark: '#1f2a17', light: '#d9c57a' },
    { skin: '#a9c5a0', hair: '#000', body: '#3e6f5c', bodyShade: '#2c5243', accent: '#5e8f7a', limb: '#a9c5a0', dark: '#162620', light: '#e8d9a8' },
    { skin: '#c7b27a', hair: '#000', body: '#8a5a2b', bodyShade: '#6a431f', accent: '#b07a3d', limb: '#c7b27a', dark: '#2a1a0f', light: '#f0e0b0' },
  ],
  parts: [
    { role: 'tail', shape: 'path', path: leftTriangle, x: 28, y: 104, width: 40, height: 20, color: 'skin', pivot: 'right', z: 1 },
    { role: 'leg.back.far', shape: 'circle', x: 100, y: 110, width: 34, height: 44, color: 'bodyShade', pivot: 'top', z: 1 },
    { role: 'leg.front.far', shape: 'circle', x: 200, y: 110, width: 34, height: 44, color: 'bodyShade', pivot: 'top', z: 1 },
    { role: 'head', shape: 'circle', x: 226, y: 78, width: 62, height: 52, color: 'skin', pivot: 'left', z: 2 },
    { role: 'plastron', shape: 'rectangle', x: 72, y: 112, width: 160, height: 22, color: 'light', z: 2 },
    { role: 'shell', shape: 'circle', x: 60, y: 22, width: 182, height: 108, color: 'body', z: 3 },
    { role: 'shell.pattern', shape: 'circle', x: 96, y: 40, width: 110, height: 60, color: 'accent', optional: true, z: 4 },
    { role: 'leg.back.near', shape: 'circle', x: 76, y: 114, width: 36, height: 46, color: 'skin', pivot: 'top', z: 5 },
    { role: 'leg.front.near', shape: 'circle', x: 176, y: 114, width: 36, height: 46, color: 'skin', pivot: 'top', z: 5 },
    { role: 'eye', shape: 'circle', x: 262, y: 90, width: 8, height: 8, color: 'dark', noOutline: true, rigid: true, z: 6 },
    { role: 'mouth', shape: 'path', path: flatMouth, x: 266, y: 108, width: 14, height: 4, color: 'dark', strokeOnly: true, strokeWidth: 2, rigid: true, z: 6 },
  ],
};

const OCTOPUS: PresetSpec = {
  id: 'octopus',
  width: 260,
  height: 270,
  palettes: [
    { skin: '#000', hair: '#000', body: '#b85c9e', bodyShade: '#8e4479', accent: '#f1a7d6', limb: '#b85c9e', dark: '#2a1a2a', light: '#ffffff' },
    { skin: '#000', hair: '#000', body: '#e0683f', bodyShade: '#b84f2c', accent: '#ffb089', limb: '#e0683f', dark: '#2a1a12', light: '#ffffff' },
    { skin: '#000', hair: '#000', body: '#3f7fbf', bodyShade: '#2f6194', accent: '#8ec1f0', limb: '#3f7fbf', dark: '#13202e', light: '#ffffff' },
    { skin: '#000', hair: '#000', body: '#59a86d', bodyShade: '#3f7d4f', accent: '#a7e3b4', limb: '#59a86d', dark: '#13261a', light: '#ffffff' },
  ],
  parts: [
    { role: 'arm.1', shape: 'path', path: armCurlLeft, x: 4, y: 150, width: 56, height: 112, color: 'bodyShade', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 1 },
    { role: 'arm.2', shape: 'path', path: armCurlRight, x: 34, y: 156, width: 56, height: 108, color: 'body', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 2 },
    { role: 'arm.3', shape: 'path', path: armCurlLeft, x: 64, y: 160, width: 56, height: 104, color: 'bodyShade', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 1 },
    { role: 'arm.4', shape: 'path', path: armCurlRight, x: 94, y: 162, width: 56, height: 100, color: 'body', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 2 },
    { role: 'arm.5', shape: 'path', path: armCurlLeft, x: 110, y: 162, width: 56, height: 100, color: 'bodyShade', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 1 },
    { role: 'arm.6', shape: 'path', path: armCurlRight, x: 140, y: 160, width: 56, height: 104, color: 'body', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 2 },
    { role: 'arm.7', shape: 'path', path: armCurlLeft, x: 170, y: 156, width: 56, height: 108, color: 'bodyShade', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 1 },
    { role: 'arm.8', shape: 'path', path: armCurlRight, x: 200, y: 150, width: 56, height: 112, color: 'body', strokeOnly: true, strokeWidth: 14, pivot: 'top', z: 2 },
    { role: 'mantle', shape: 'circle', x: 56, y: 10, width: 148, height: 156, color: 'body', pivot: 'bottom', z: 3 },
    { role: 'mantle.shade', shape: 'circle', x: 86, y: 120, width: 88, height: 36, color: 'accent', noOutline: true, optional: true, z: 4 },
    { role: 'eye.white.left', shape: 'circle', x: 90, y: 72, width: 26, height: 28, color: 'light', rigid: true, z: 5 },
    { role: 'eye.white.right', shape: 'circle', x: 144, y: 72, width: 26, height: 28, color: 'light', rigid: true, z: 5 },
    { role: 'eye.left', shape: 'circle', x: 98, y: 80, width: 12, height: 14, color: 'dark', noOutline: true, rigid: true, z: 6 },
    { role: 'eye.right', shape: 'circle', x: 152, y: 80, width: 12, height: 14, color: 'dark', noOutline: true, rigid: true, z: 6 },
    { role: 'mouth', shape: 'path', path: smile, x: 120, y: 112, width: 20, height: 5, color: 'dark', strokeOnly: true, strokeWidth: 2, rigid: true, z: 6 },
  ],
};

const PRESETS: Record<CharacterPresetId, PresetSpec> = {
  humanoid: HUMANOID,
  animal: ANIMAL,
  turtle: TURTLE,
  octopus: OCTOPUS,
};

export function getCharacterPresetSize(preset: CharacterPresetId): { width: number; height: number } {
  const spec = PRESETS[preset];
  return { width: spec.width, height: spec.height };
}

// ---------------------------------------------------------------------------
// Deterministik rastgelelik
// ---------------------------------------------------------------------------

/** Ardışık/küçük seed'ler PRNG'nin ilk çıktısında kümelenmesin diye avalanche karıştırması. */
function scrambleSeed(seed: number): number {
  let x = (seed ^ 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  return (x ^ (x >>> 16)) >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = scrambleSeed(seed);
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function randomCharacterSeed(): number {
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}

/** Gövde ölçeğinin dışına taşan parçaları dışarıda bırakmamak için ortadan ölçekle. */
function jitterBox(
  part: PartSpec,
  factor: number
): { x: number; y: number; width: number; height: number } {
  const cx = part.x + part.width / 2;
  const cy = part.y + part.height / 2;
  const w = part.width * factor;
  const h = part.height * factor;
  return { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
}

// ---------------------------------------------------------------------------
// Üretim
// ---------------------------------------------------------------------------

const OUTLINE_WIDTH = 2;

/**
 * Preset ve seed'den parça listesi üretir. Koordinatlar grubun yerel
 * uzayındadır; grup kutusu `targetHeight` ile ölçeklenir.
 */
export function generateCharacter(
  preset: CharacterPresetId,
  seed: number,
  targetHeight?: number
): GeneratedCharacter {
  const spec = PRESETS[preset];
  const rand = mulberry32(seed);

  const palette = spec.palettes[Math.floor(rand() * spec.palettes.length)] ?? spec.palettes[0]!;
  const scale = targetHeight && targetHeight > 0 ? targetHeight / spec.height : 1;

  const parts: GeneratedPart[] = [];

  for (const part of spec.parts) {
    if (part.optional && rand() < 0.35) continue;

    // Parça boyutunda %±6 titreme; göz/ağız gibi rigid parçalar sabit.
    const factor = part.rigid ? 1 : 0.94 + rand() * 0.12;
    const box = jitterBox(part, factor);

    const width = Math.max(2, Math.round(box.width * scale));
    const height = Math.max(2, Math.round(box.height * scale));
    const x = Math.round(box.x * scale);
    const y = Math.round(box.y * scale);

    const color = palette[part.color];
    const strokeOnly = part.strokeOnly === true;
    const outline = !strokeOnly && !part.noOutline;

    parts.push({
      role: part.role,
      shapeType: part.shape,
      pathData: part.path ? part.path(width, height) : undefined,
      x,
      y,
      width,
      height,
      rotation: part.rotation ?? 0,
      pivotPoint: part.pivot ?? 'center',
      fill: strokeOnly ? undefined : color,
      fillMode: strokeOnly ? 'none' : 'solid',
      stroke: strokeOnly ? color : palette.dark,
      strokeWidth: strokeOnly ? Math.max(1, Math.round((part.strokeWidth ?? 2) * scale)) : outline ? OUTLINE_WIDTH : 0,
      strokeStyle: strokeOnly || outline ? 'solid' : 'none',
      zIndex: part.z,
    });
  }

  // Aynı z'deki parçalar liste sırasını korusun; çocuk sorter'ı bu sıradan türetilir.
  parts.sort((a, b) => a.zIndex - b.zIndex);
  parts.forEach((p, index) => {
    p.zIndex = index;
  });

  return {
    preset,
    seed,
    width: Math.round(spec.width * scale),
    height: Math.round(spec.height * scale),
    parts,
  };
}
